import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDown,
  faAngleUp,
  faAnglesUpDown,
  faCheck,
  faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Select from '@radix-ui/react-select';
import { Controller, useFormContext } from 'react-hook-form';
import { cn } from '~/helpers/utils';
import { SelectFieldProps } from './SelectField';

const ExtendedSelect: React.FC<
  React.ComponentPropsWithoutRef<typeof Select.Root>
> = ({ children, ...props }) => {
  return (
    <Select.Root {...props}>
      <Select.Trigger className="form-input relative appearance-none !py-[0.75rem] !pr-10 text-left">
        <Select.Value className="text" />
        <Select.Icon className="accent-color pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm">
          <FontAwesomeIcon icon={faAnglesUpDown} />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="z-50 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-stone-200 bg-white shadow-lg"
          position="popper"
          sideOffset={5}
        >
          <Select.ScrollUpButton className="flex h-6 cursor-pointer items-center justify-center bg-white hover:bg-gray-50">
            <FontAwesomeIcon icon={faAngleUp} className="text-blue-500" />
          </Select.ScrollUpButton>
          <Select.Viewport>{children}</Select.Viewport>
          <Select.ScrollDownButton className="flex h-6 cursor-pointer items-center justify-center bg-white hover:bg-gray-50">
            <FontAwesomeIcon icon={faAngleDown} className="text-blue-500" />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

type ExtendedSelectItem = React.ComponentPropsWithoutRef<typeof Select.Item> & {
  isLoading?: boolean;
};

export const ExtendedSelectItem: React.FC<ExtendedSelectItem> = ({
  children,
  disabled,
  isLoading,
  ...props
}) => {
  return (
    <Select.Item
      {...props}
      disabled={disabled}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded px-8 py-2 text-sm outline-none',
        disabled
          ? 'text-stone-300 hover:cursor-not-allowed'
          : 'hover:bg-gray-100 focus:bg-gray-100 data-[highlighted]:bg-gray-100'
      )}
    >
      <Select.ItemText>
        <div className="flex">
          {children}
          {isLoading && (
            <span className="ml-2">
              <FontAwesomeIcon
                icon={faSpinnerThird as IconProp}
                spin={true}
                fontSize={'9'}
                className="text-blue-500"
              />
            </span>
          )}
        </div>
      </Select.ItemText>
      <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
        <FontAwesomeIcon icon={faCheck} className="text-blue-500" />
      </Select.ItemIndicator>
    </Select.Item>
  );
};

const ExtendedSelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  options,
}) => {
  const { control } = useFormContext();

  const inputId = `field-${name}`;
  const defaultValue = options.find((opt) => opt.selected)?.value ?? '';

  return (
    <div className="form-control col-span-1">
      <div className="flex">
        <label className="form-label" htmlFor={inputId}>
          {label}
        </label>
      </div>
      <div className="relative">
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => (
            <ExtendedSelect
              name={name}
              value={field.value}
              defaultValue={defaultValue}
              onValueChange={field.onChange}
            >
              {options.map((opt) => (
                <ExtendedSelectItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled || opt.isLoading}
                  isLoading={opt.isLoading}
                >
                  {opt.label}
                </ExtendedSelectItem>
              ))}
            </ExtendedSelect>
          )}
        />
      </div>
    </div>
  );
};

export default ExtendedSelectField;
